<template>
  <div class="max-w-4xl mx-auto p-6">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">TaxJar API Endpoints</h1>

    <div class="space-y-6">
      <!-- Main Index -->
      <div class="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
        <h2 class="text-xl font-semibold text-gray-700 mb-3">Main Endpoint</h2>
        <div
          @click="copyToClipboard(baseUrl + '/taxjar')"
          class="text-blue-600 hover:text-blue-800 font-mono text-lg break-all cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
          :class="{
            'bg-green-50 text-green-700': copiedUrl === baseUrl + '/taxjar'
          }"
        >
          {{ baseUrl }}/taxjar
          <span v-if="copiedUrl === baseUrl + '/taxjar'" class="ml-2 text-sm"
            >✓ Copied!</span
          >
        </div>
        <p class="text-gray-600 mt-2">
          Get TaxJar categories and summary rates
        </p>
      </div>

      <!-- Send Single Order -->
      <div
        class="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500"
      >
        <h2 class="text-xl font-semibold text-gray-700 mb-3">
          Send Single Order
        </h2>
        <div
          @click="copyToClipboard(baseUrl + '/taxjar/send_order/ORDER123')"
          class="text-green-600 hover:text-green-800 font-mono text-lg break-all cursor-pointer hover:bg-green-50 p-2 rounded transition-colors"
          :class="{
            'bg-green-50 text-green-700':
              copiedUrl === baseUrl + '/taxjar/send_order/ORDER123'
          }"
        >
          {{ baseUrl }}/taxjar/send_order/{order_number}
          <span
            v-if="copiedUrl === baseUrl + '/taxjar/send_order/ORDER123'"
            class="ml-2 text-sm"
            >✓ Copied!</span
          >
        </div>
        <p class="text-gray-600 mt-2">
          Send or update a specific order to TaxJar
        </p>
        <p class="text-sm text-gray-500 mt-1">
          Replace ORDER123 with actual order number
        </p>
      </div>

      <!-- Backup Table -->
      <div
        class="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500"
      >
        <h2 class="text-xl font-semibold text-gray-700 mb-3">
          Get Backup Table
        </h2>
        <div
          @click="copyToClipboard(baseUrl + '/taxjar/backup_table')"
          class="text-purple-600 hover:text-purple-800 font-mono text-lg break-all cursor-pointer hover:bg-purple-50 p-2 rounded transition-colors"
          :class="{
            'bg-green-50 text-green-700':
              copiedUrl === baseUrl + '/taxjar/backup_table'
          }"
        >
          {{ baseUrl }}/taxjar/backup_table
          <span
            v-if="copiedUrl === baseUrl + '/taxjar/backup_table'"
            class="ml-2 text-sm"
            >✓ Copied!</span
          >
        </div>
        <p class="text-gray-600 mt-2">Get TaxJar rates backup table data</p>
      </div>

      <!-- Recreate Backup Table -->
      <div
        class="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500"
      >
        <h2 class="text-xl font-semibold text-gray-700 mb-3">
          Recreate Backup Table
        </h2>
        <div
          @click="copyToClipboard(baseUrl + '/taxjar/recreate_backup_table')"
          class="text-orange-600 hover:text-orange-800 font-mono text-lg break-all cursor-pointer hover:bg-orange-50 p-2 rounded transition-colors"
          :class="{
            'bg-green-50 text-green-700':
              copiedUrl === baseUrl + '/taxjar/recreate_backup_table'
          }"
        >
          {{ baseUrl }}/taxjar/recreate_backup_table
          <span
            v-if="copiedUrl === baseUrl + '/taxjar/recreate_backup_table'"
            class="ml-2 text-sm"
            >✓ Copied!</span
          >
        </div>
        <p class="text-gray-600 mt-2">
          Recreate backup table with current TaxJar rates
        </p>
      </div>

      <!-- Sync Orders -->
      <div class="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
        <h2 class="text-xl font-semibold text-gray-700 mb-3">Sync Orders</h2>
        <div
          @click="
            copyToClipboard(
              baseUrl +
                '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=100'
            )
          "
          class="text-red-600 hover:text-red-800 font-mono text-lg break-all cursor-pointer hover:bg-red-50 p-2 rounded transition-colors"
          :class="{
            'bg-green-50 text-green-700':
              copiedUrl ===
              baseUrl +
                '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=100'
          }"
        >
          {{
            baseUrl
          }}/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=100
          <span
            v-if="
              copiedUrl ===
                baseUrl +
                  '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=100'
            "
            class="ml-2 text-sm"
            >✓ Copied!</span
          >
        </div>
        <p class="text-gray-600 mt-2">
          Sync multiple orders within a date range
        </p>
        <div class="mt-3 text-sm text-gray-600">
          <p><strong>Parameters:</strong></p>
          <ul class="list-disc list-inside ml-4 space-y-1">
            <li>
              <code class="bg-gray-100 px-1 rounded">from_date</code> (required)
              - Start date (YYYY-MM-DD)
            </li>
            <li>
              <code class="bg-gray-100 px-1 rounded">to_date</code> (required) -
              End date (YYYY-MM-DD)
            </li>
            <li>
              <code class="bg-gray-100 px-1 rounded">limit</code> (optional) -
              Max orders to process (default: 10)
            </li>
          </ul>
        </div>
      </div>

      <!-- Example URLs -->
      <div class="bg-gray-50 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Quick Examples</h3>
        <div class="space-y-3">
          <div
            @click="
              copyToClipboard(
                baseUrl +
                  '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=50'
              )
            "
            class="text-blue-600 hover:text-blue-800 font-mono text-sm cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
            :class="{
              'bg-green-50 text-green-700':
                copiedUrl ===
                baseUrl +
                  '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=50'
            }"
          >
            Sync last 50 orders from 2024
            <span
              v-if="
                copiedUrl ===
                  baseUrl +
                    '/taxjar/sync_orders?from_date=2024-01-01&to_date=2024-12-31&limit=50'
              "
              class="ml-2 text-sm"
              >✓ Copied!</span
            >
          </div>
          <div
            @click="
              copyToClipboard(
                baseUrl +
                  '/taxjar/sync_orders?from_date=2024-03-01&to_date=2024-03-31&limit=25'
              )
            "
            class="text-blue-600 hover:text-blue-800 font-mono text-sm cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
            :class="{
              'bg-green-50 text-green-700':
                copiedUrl ===
                baseUrl +
                  '/taxjar/sync_orders?from_date=2024-03-01&to_date=2024-03-31&limit=25'
            }"
          >
            Sync March 2024 orders (max 25)
            <span
              v-if="
                copiedUrl ===
                  baseUrl +
                    '/taxjar/sync_orders?from_date=2024-03-01&to_date=2024-03-31&limit=25'
              "
              class="ml-2 text-sm"
              >✓ Copied!</span
            >
          </div>
          <div
            @click="copyToClipboard(baseUrl + '/taxjar/send_order/12345')"
            class="text-blue-600 hover:text-blue-800 font-mono text-sm cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
            :class="{
              'bg-green-50 text-green-700':
                copiedUrl === baseUrl + '/taxjar/send_order/12345'
            }"
          >
            Send order #12345 to TaxJar
            <span
              v-if="copiedUrl === baseUrl + '/taxjar/send_order/12345'"
              class="ml-2 text-sm"
              >✓ Copied!</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSelectedStoreURL } from "@/api/httpStore.js";

export default {
  name: "TaxjarRoutes",
  data() {
    return {
      copiedUrl: null
    };
  },
  computed: {
    baseUrl() {
      return getSelectedStoreURL() || process.env.VUE_APP_MAIN_ADMIN_API;
    }
  },
  methods: {
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedUrl = text;

        // Clear the copied state after 2 seconds
        setTimeout(() => {
          this.copiedUrl = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        this.copiedUrl = text;
        setTimeout(() => {
          this.copiedUrl = null;
        }, 2000);
      }
    }
  }
};
</script>
